/**
 * Upload Service
 * Handles file uploads to AWS S3 via backend API
 */

// Use API Gateway (port 3000) - it routes to auth-service internally
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

/**
 * Upload avatar image to AWS S3 via backend
 * @param file - Image file to upload
 * @param userId - User ID for file path
 * @returns S3 URL of uploaded image
 */
export async function uploadAvatar(file: File, userId: string): Promise<string> {
  // Validate file type
  const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg', 'image/gif']
  if (!validTypes.includes(file.type)) {
    throw new Error('Please upload a JPG, PNG, WEBP, or GIF image')
  }

  // Validate file size (5MB max)
  const maxSize = 5 * 1024 * 1024 // 5MB in bytes
  if (file.size > maxSize) {
    throw new Error('Image must be under 5MB')
  }

  // Create FormData for multipart/form-data upload
  const formData = new FormData()
  formData.append('avatar', file)
  formData.append('userId', userId)

  // Upload to backend (which uploads to AWS S3)
  const response = await fetch(`${API_URL}/api/avatar/upload`, {
    method: 'POST',
    body: formData
    // Note: Do NOT set Content-Type header - browser will set it with boundary
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Upload failed' }))
    console.error('[uploadService] Avatar upload error:', errorData)
    throw new Error(errorData.error || 'Failed to upload avatar. Please try again.')
  }

  const result = await response.json()

  // Return the S3 key (not the signed URL) - backend will generate fresh URLs on-the-fly
  return result.avatar.s3_key
}

/**
 * Delete avatar from AWS S3 via backend
 * @param userId - User ID
 * @param s3Key - S3 key of avatar to delete
 */
export async function deleteAvatar(userId: string, s3Key: string): Promise<void> {
  const response = await fetch(`${API_URL}/api/avatar/${userId}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ s3Key })
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Delete failed' }))
    console.error('[uploadService] Avatar delete error:', errorData)
    throw new Error(errorData.error || 'Failed to delete avatar')
  }

}
